import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  private defaultDuration = 5000; // 5 seconds

  /**
   * Show a toast notification
   * @param message - The description/body text
   * @param type - The type of toast (success, error, warning, info)
   * @param duration - Duration in milliseconds (0 = no auto-dismiss)
   * @param title - Optional title shown above the description
   */
  show(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration?: number,
    title?: string
  ): void {
    const toastDuration = duration ?? this.defaultDuration;
    const toast: Toast = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      title,
      message,
      type,
      duration: toastDuration
    };

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast]);
  }

  /**
   * Show a success toast
   */
  success(message: string, duration?: number, title?: string): void {
    this.show(message, 'success', duration, title);
  }

  /**
   * Show an error toast
   */
  error(message: string, duration?: number, title?: string): void {
    this.show(message, 'error', duration, title);
  }

  /**
   * Show a warning toast
   */
  warning(message: string, duration?: number, title?: string): void {
    this.show(message, 'warning', duration, title);
  }

  /**
   * Show an info toast
   */
  info(message: string, duration?: number, title?: string): void {
    this.show(message, 'info', duration, title);
  }

  /**
   * Remove a toast by ID (component handles fade-out animation)
   */
  remove(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toastsSubject.next([]);
  }
}
